/* globals process */

import {
  api,
  furtherAuthAPI,
  frontend,
  pdf,
  sso,
  storage,
  taxDocs,
  FURTHER_MEMBER_AUTHENTICATION_API_SUB_URLS,
  MEMBER_API_SUB_URLS,
} from '@/api';
import Cookies from 'js-cookie';
import store from '@/store';
import { Auth } from '@aws-amplify/auth';
import { DemoService } from '@/services';
import BRANDS from '../../config/branding/brands';

export default {
  // -------------------------------
  // PUBLIC METHODS
  // -------------------------------
  // Sign In Method takes creds and authenticates
  async signIn(CREDENTIALS) {
     // Call Cognito to get JWT Token
      const user = await Auth.signIn(CREDENTIALS.username, CREDENTIALS.password);
      if (!DemoService.isTestSite() && user.challengeName === 'SMS_MFA') {
        const MFA_CODE = await api.post(`${MEMBER_API_SUB_URLS.mfaCodes}`, {userId: user.username} );
        if (MFA_CODE.data.mfaCode === 'SENT_TO_EMAIL') {
          return { message: 'mfa-challenge', response: user };
        }
        const JWT_TOKEN_RESPONSE = await Auth.confirmSignIn(user, MFA_CODE.data.mfaCode);
        await this.setCookie(JWT_TOKEN_RESPONSE.signInUserSession.idToken.jwtToken);
      } else {
        await this.setCookie(user.signInUserSession.idToken.jwtToken);
      }
  },
  async respondToMFAChallenge(MFACode, challengeResponse, rememberDevice) {
    return new Promise((resolve, reject) => {
      Auth.confirmSignIn(
        challengeResponse,
        MFACode,
        challengeResponse.challengeName,
      )
        .then(async (RESPONSE) => {
          if (rememberDevice) {
            await new Promise((res, rej) => {
              challengeResponse.setDeviceStatusRemembered({
                onSuccess: function(result) {
                  res(result);
                },
                onFailure: function(err) {
                  res(err);
                },
              });
            });
          }
          this.setCookie(RESPONSE.signInUserSession.idToken.jwtToken);
          resolve();
        })
        .catch(reject);
    });
  },
  getSession() { 
    return new Promise(async (resolve, reject) => {
      // Get the url, make it a string
      const URL = window.location.toString();

      // Look for the id_token
      const JWT_TOKEN = URL.match(/id_token=([^&]+)/);

      // Look for multiple group flag
      let MULTIPLE_GROUPS = URL.match(/is_temp=true/);

      // Look for refresh_token
      const REFRESH_TOKEN = URL.match(/refresh_token=([^&]+)/);

      // Look for identity_provider for OIDC federation
      const identity_provider = URL.match(/identity_provider=([^&]+)/);

      // Look for auth code
      const CODE = URL.match(/code=([^&]+)/);

      const redirect_uri = encodeURIComponent(`${window.location.origin}/portal/accounts`);

      const JWT = this.getCookie();

      // Look for role and memberId from session for Classic SSO
      let MEMBER_MIGRATION_COMPLETE = false

      let session;

      if((JWT || JWT_TOKEN) && !(MULTIPLE_GROUPS || identity_provider || CODE)) {
        await api.get(FURTHER_MEMBER_AUTHENTICATION_API_SUB_URLS.session, {
          headers: {
            Authorization: JWT_TOKEN ? JWT_TOKEN[1] : JWT
          }
        }).then(response => {
          session = response.data;
          MULTIPLE_GROUPS = response.data.role === 'MemberSelect' ? true : false;
          MEMBER_MIGRATION_COMPLETE = response.data.role === 'MemberMigrationComplete' ? true : false;
        }).catch(err => {});
      }

      if (JWT_TOKEN && MULTIPLE_GROUPS && REFRESH_TOKEN) {
        // If we have both a temp token and the multiple groups flag, call this
        this.hasMultipleGroups(JWT_TOKEN[1], REFRESH_TOKEN[1]);

        // Reject, because the user needs futher authentication work done
        reject();
      } else if (MULTIPLE_GROUPS === true) { //MULTIPLE_GROUPS being boolean rather than match array means we pulled from session API
        this.hasMultipleGroups(JWT, Cookies.get('refreshToken'));

        reject();
      } else if (MEMBER_MIGRATION_COMPLETE) {
        // If role is memberMigrated set userJWT and memberId for SSO to classic
        this.finishHQYSSOWithToken(JWT_TOKEN ? JWT_TOKEN[1] : JWT)
          .then(reject)
          .catch(reject);
      } else if (JWT_TOKEN) {
        // If we just have a JWT, call this
        this.finishAuthWithToken(JWT_TOKEN[1], session)
          .then((user) => {
            resolve(user);
          })
          .catch(reject);
      } else if (identity_provider) {
        Auth.federatedSignIn({
          customProvider: `${this.getCustomProvider(identity_provider)}`,
        }).then(() => {
          return Auth.currentAuthenticatedUser();
        }).then(() => {
          return this.getToken();
        }).then((token) => {
          this.setCookie(token);
          Cookies.set('federatedSignIn', true);
          this.clearCognitoLocalStorage();
          window.location = `https://${process.env.VUE_APP_COGNITO_OAUTH_URL}/logout?client_id=${Auth.configure().userPoolWebClientId}&logout_uri=${redirect_uri}`;
        });
        // If passing code param it will not try to use a previous cookie when authenticating
      } else if (CODE) {
        reject();
      } else {
        // No token in URL, so check for a cookie
        const COOKIE = this.getCookie();
        if (COOKIE) {
          // Cookie contains the token, continue authentication
          this.finishAuthWithToken(COOKIE, session)
            .then((user) => {
              resolve(user);
            })
            .catch(reject);
        } else {
          reject();
        }
      }
    });
  },
  // -------------------------------
  // PRIVATE METHODS
  // -------------------------------
  // The user has a token in the url, set the headers and continue with user object construction
  finishAuthWithToken(JWT_TOKEN, session) {
    this.setAuthorizationToken(JWT_TOKEN);
    return this.constructUserObject({
      jwt: JWT_TOKEN,
    }, session);
  },
  async finishHQYSSOWithToken(JWT_TOKEN) {
      this.setAuthorizationToken(JWT_TOKEN);
      store.dispatch('setMemberMigrationComplete');
      await store.dispatch('setUserObjectWithToken', { jwt: JWT_TOKEN });
  },
  hasMultipleGroups(JWT_TOKEN, REFRESH_TOKEN) {
    // the user needs to choose a group, redirect to the group selection page
    this.setAuthorizationToken(JWT_TOKEN);
    Cookies.set('refreshToken', REFRESH_TOKEN, {
      secure: true
    });
    store.dispatch('setChooseGroup');
  },
  getCookie() {
    return Cookies.get('idToken');
  },
  setCookie(JWT_TOKEN) {
    // Adding Secure attribute to cookie.
    // @docs https://jira.hellofurther.net/browse/AG-63
    Cookies.set('idToken', JWT_TOKEN, {
      secure: true
    });
  },
  deleteCookie() {
    Cookies.remove('idToken');
    Cookies.remove('cognito');
    Cookies.remove('refreshToken');
  },
  setAuthorizationToken(JWT_TOKEN) {
    api.defaults.headers['Authorization'] = JWT_TOKEN;
    furtherAuthAPI.defaults.headers['Authorization'] = JWT_TOKEN;
    frontend.defaults.headers['Authorization'] = JWT_TOKEN;
    pdf.defaults.headers['Authorization'] = JWT_TOKEN;
    sso.defaults.headers['Authorization'] = JWT_TOKEN;
    storage.defaults.headers['Authorization'] = JWT_TOKEN;
    taxDocs.defaults.headers['Authorization'] = JWT_TOKEN;
  },
  // Check domain will ensure the user is on the correct site and will redirect if needed
  checkDomain(JWT_TOKEN) {
    return new Promise((resolve, reject) => {
      frontend
        .get('/get-primary-domain')
        .then((result) => {
          const PRIMARY_URL = result.data.primaryURL;
          if (PRIMARY_URL && !window.location.href.startsWith(PRIMARY_URL)) {
            // Delete idToken cookie from this domain before sending session over to branded URL
            this.deleteCookie();
            // Are you changing #id_token? See also src/services/auth.service.js
            window.location =
              result.data.primaryURL +
              (PRIMARY_URL.endsWith('/') ? '' : '/') +
              'portal/?id_token=' +
              frontend.defaults.headers['Authorization'];
            reject();
          } else {
            resolve();
          }
        })
        .catch(() => {
          resolve();
        });
    });
  },
  setUserObject(USER) {
    return new Promise((resolve, reject) => {
      api
      .get(`${MEMBER_API_SUB_URLS.members}/me/id`)
      .then((memberId) => {
              // Put memberId and userJWT into user object
              const userObject = {
                ...memberId.data,
                ...USER,
              };
              this.setCookie(USER.jwt);
              resolve(userObject);
            })
            .catch(reject);
          })
  },
  // Construct user object makes the remaining auth calls and commits the user object to the store
  constructUserObject(USER, session) {
    return new Promise((resolve, reject) => {
      // Whitelabeling: force the user onto the domain that matches their distribution partner
      const domainCheck = this.checkDomain(USER.jwt);

      // Get the user ID, this is needed for the rest of the calls
      api
        .get(`${MEMBER_API_SUB_URLS.members}/me/id`)
        .then((memberId) => {

          const API_CALLS = [
            api.get(`${MEMBER_API_SUB_URLS.members}/${memberId.data.memberId}/headers`),
            api.get(`${MEMBER_API_SUB_URLS.users}/me/roles`),
          ];

          Promise.all(API_CALLS)
            .then((response) => {
              // Check if the user is fraudulant, if so, reject the authentication
              if (response[0].data.isFraudulant) {
                reject('fraudulant');
                return;
              }

              // Put together all of the member data collected from the various api responses
              const userObject = {
                ...memberId.data,
                ...USER,
                ...response[0].data, // member-api/${process.env.VUE_APP_API_VERSION}/members/${memberId.data.memberId}/headers
                ...response[1].data, // member-api/${process.env.VUE_APP_API_VERSION}/users/me/roles
                ...{
                  session,
                },
              };


              if (
                (BRANDS[userObject.distributionPartnerCode.toLowerCase()] && BRANDS[userObject.distributionPartnerCode.toLowerCase()].cobranded)
                && process.env.VUE_APP_CLIENT_DPC !== userObject.distributionPartnerCode.toLowerCase()
                && process.env.VUE_APP_API_VERSION !== 'dev'
              ) {
                window.location.reload();
              }

              // Check the domain for whitelabeling
              domainCheck.then(() => {
                /* Set cookie here to make sure all APIs were successful because primary domain will return successfully
                   for expired JWTs which are still found in frontend REDIS cache. */
                this.setCookie(USER.jwt);
                resolve(userObject);
              });
            })
            .catch(reject);
        })
        .catch(reject);
    });
  },
  // Sign out the user
  async signOutUser(signedOut) {
    if (signedOut) {
      /* When user physically signs-out, using await, otherwise, the delete request never finishes
      before leaving the page, causing the request to get cancelled and never complete. */
      await api.delete(
        FURTHER_MEMBER_AUTHENTICATION_API_SUB_URLS.tokens
      );
    }
    // Delete cookies here in case amplify does an oauth signout redirect next
    this.deleteCookie();
    Cookies.remove('questionsOpened');
    await Auth.signOut();
    this.signOutRedirect(signedOut);
  },
  // For federated Single Logut
  async singleSignOutUser(signedOut) {
    if (signedOut) {
      await api.delete(
        FURTHER_MEMBER_AUTHENTICATION_API_SUB_URLS.tokens
      );
    }
    let redirectUrl = this.getCookie().split('.')[1];
    redirectUrl = redirectUrl.replace(/-/g, '+').replace(/_/g, '/');
    redirectUrl = JSON.parse(atob(redirectUrl));

    // Delete cookies here in case amplify does an oauth signout redirect next
    this.deleteCookie();
    Cookies.remove('questionsOpened');
    Auth.signOut();
    localStorage.clear();
    window.location = redirectUrl['custom:partner_logout_uri']
  },
  signOutRedirect(signedOut) {
    // Redirect user
    const signedOutString = signedOut ? '?signed_out=true' : '';

    // Full page change, to clear memory
    window.location = `/portal/logged-out${signedOutString}`;
  },
  getToken() {
    return Auth.currentSession()
      .then((session) => {
        return session.getIdToken().getJwtToken();
      })
      .catch(err => console.log(err));
  },
  clearCognitoLocalStorage() {
    Object.keys(localStorage).filter(key => key.startsWith('CognitoIdentityServiceProvider')).forEach(key => localStorage.removeItem(key));
  },
  async userLoggedIn() {
    try {
      await Auth.currentAuthenticatedUser()
      console.log('user logged in true');
        return true
    } catch(e) {
      console.log('user logged in false');
        return false
      };
  },
  // For non-co-branded SSO Partners, check identity_provider param being passed to set custom provider
  getCustomProvider(identityProvider) {
    if (identityProvider[1].toUpperCase() === 'HIGHMARK') {
      return 'Highmark';
    }
    return process.env.VUE_APP_COGNITO_PARTNER_PROVIDER;
  },
};
